/**
 * Serwis API dla modulu Kosztow Zewnetrznych i Rentownosci
 */

import api from './api';

export const externalCostsService = {
  // ==========================================
  // KATEGORIE KOSZTOW
  // ==========================================

  getCategories: async (aktywny = 1) => {
    try {
      const response = await api.get(`external-costs/categories?aktywny=${aktywny}`);
      return response.data;
    } catch (error) {
      console.error('Blad pobierania kategorii:', error);
      throw error;
    }
  },

  createCategory: async (data) => {
    try {
      const response = await api.post('external-costs/categories', data);
      return response.data;
    } catch (error) {
      console.error('Blad tworzenia kategorii:', error);
      throw error;
    }
  },

  updateCategory: async (id, data) => {
    try {
      const response = await api.put(`external-costs/categories/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Blad aktualizacji kategorii:', error);
      throw error;
    }
  },

  deleteCategory: async (id) => {
    try {
      const response = await api.delete(`external-costs/categories/${id}`);
      return response.data;
    } catch (error) {
      console.error('Blad usuwania kategorii:', error);
      throw error;
    }
  },

  // ==========================================
  // KOSZTY ZEWNETRZNE
  // ==========================================

  getCosts: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.location_id) params.append('location_id', filters.location_id);
      if (filters.category_id) params.append('category_id', filters.category_id);
      if (filters.status) params.append('status', filters.status);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.cykliczny !== undefined) params.append('cykliczny', filters.cykliczny);
      // Etap 2 (koszty z KSeF): NIP kontrahenta, zrodlo (reczny|ksef|faktura_zakupowa), tylko przeterminowane
      if (filters.nip) params.append('nip', filters.nip);
      if (filters.zrodlo) params.append('zrodlo', filters.zrodlo);
      if (filters.przeterminowane) params.append('przeterminowane', '1');
      if (filters.page) params.append('page', filters.page);
      if (filters.per_page) params.append('per_page', filters.per_page);

      const response = await api.get(`external-costs?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Blad pobierania kosztow:', error);
      throw error;
    }
  },

  getCost: async (id) => {
    try {
      const response = await api.get(`external-costs/${id}`);
      return response.data;
    } catch (error) {
      console.error('Blad pobierania kosztu:', error);
      throw error;
    }
  },

  createCost: async (data) => {
    try {
      const response = await api.post('external-costs', data);
      return response.data;
    } catch (error) {
      console.error('Blad tworzenia kosztu:', error);
      throw error;
    }
  },

  updateCost: async (id, data) => {
    try {
      const response = await api.put(`external-costs/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Blad aktualizacji kosztu:', error);
      throw error;
    }
  },

  deleteCost: async (id) => {
    try {
      const response = await api.delete(`external-costs/${id}`);
      return response.data;
    } catch (error) {
      console.error('Blad usuwania kosztu:', error);
      throw error;
    }
  },

  payCost: async (id, data = {}) => {
    try {
      const response = await api.post(`external-costs/${id}/pay`, data);
      return response.data;
    } catch (error) {
      console.error('Blad oznaczania platnosci:', error);
      throw error;
    }
  },

  // ==========================================
  // ETAP 2: KOSZTY Z KSeF, REGULY, PODSUMOWANIE MIESIECZNE, OZNACZ ZAPLACONY
  // Kontrakt = backend/api/external_costs.py (sekcja "ETAP 2"). Bledy: interceptor api.js rzuca Error
  // z `status`, `errorCode` (error_code) i `data` (cale cialo: {komunikat, error_code, istniejacy?...}).
  // ==========================================

  /** GET /external-costs/z-ksef?strona&na_strone&nip&szukaj&od&do&rodzaj=vat|korekty&location_id
   *  -> {success, data: {wiersze[wiersz KSeF + sugestia, sugestia_kategorii, koszt_istniejacy, korygowana_koszt, mozna_zaksiegowac],
   *      razem, strona, na_strone, stron, filtry}} */
  getZKsef: async (params = {}) => {
    try {
      const czyste = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined));
      const response = await api.get('external-costs/z-ksef', { params: czyste });
      return response.data;
    } catch (error) {
      console.error('Blad listy z KSeF:', error);
      throw error;
    }
  },

  /** GET /external-costs/z-ksef/<ksef_id> -> szczegoly jak GET /ksef/otrzymane/<id> (platnosc, szczegoly_xml, pozycje...)
   *  pod uprawnieniem ksiegowosc.rozliczenia. Zwraca odpakowane `data`. */
  getZKsefSzczegoly: async (ksefId) => {
    const response = await api.get(`external-costs/z-ksef/${ksefId}`);
    return response?.data?.data ?? response?.data;
  },

  /** GET /external-costs/z-ksef/<ksef_id>/xml -> Blob (XML FA(3)); 404 XML_NIEPOBRANY. */
  pobierzXmlZKsef: async (ksefId) => {
    const response = await api.get(`external-costs/z-ksef/${ksefId}/xml`, { responseType: 'blob' });
    return response?.data;
  },

  /** POST /external-costs/z-ksef {ksef_id, category_id, location_id|null, status?, data_platnosci?, termin_platnosci?, uwagi?}
   *  -> 201 {success, data: koszt {id, ksef_id, kwota_*, termin_platnosci, koryguje_id, ostrzezenia[]}}
   *  409 KOSZT_ISTNIEJE (err.data.istniejacy), 400 ZLY_STATUS | BLAD_DANYCH, 404 NIE_ZNALEZIONO */
  ksiegujZKsef: async (body) => {
    try {
      const response = await api.post('external-costs/z-ksef', body);
      return response.data;
    } catch (error) {
      console.error('Blad ksiegowania z KSeF:', error);
      throw error;
    }
  },

  /** GET /external-costs/summary/monthly?rok&location_id ('' = wszystkie, '0' = ogolne, id = sklep) */
  getMonthlySummary: async (rok, locationId = '') => {
    try {
      const params = { rok };
      if (locationId !== '' && locationId !== null && locationId !== undefined) params.location_id = locationId;
      const response = await api.get('external-costs/summary/monthly', { params });
      return response.data;
    } catch (error) {
      console.error('Blad podsumowania miesiecznego:', error);
      throw error;
    }
  },

  getReguly: async (nip = '') => {
    try {
      const response = await api.get('external-costs/reguly', { params: nip ? { nip } : {} });
      return response.data;
    } catch (error) {
      console.error('Blad listy regul:', error);
      throw error;
    }
  },

  createRegula: async (data) => {
    try {
      const response = await api.post('external-costs/reguly', data);
      return response.data;
    } catch (error) {
      console.error('Blad zapisu reguly:', error);
      throw error;
    }
  },

  deleteRegula: async (id) => {
    try {
      const response = await api.delete(`external-costs/reguly/${id}`);
      return response.data;
    } catch (error) {
      console.error('Blad usuwania reguly:', error);
      throw error;
    }
  },

  /** GET /external-costs/sugestia-kategorii?nip&location_id -> {category_id, kategoria_nazwa, location_id, zrodlo, uzyc} */
  getSugestiaKategorii: async (nip, locationId = '') => {
    try {
      const params = { nip };
      if (locationId) params.location_id = locationId;
      const response = await api.get('external-costs/sugestia-kategorii', { params });
      return response.data;
    } catch (error) {
      console.error('Blad sugestii kategorii:', error);
      throw error;
    }
  },

  /** POST /external-costs/<id>/oznacz-zaplacony {sposob:'kw'|'konto', data?, typ_platnosci?}
   *  -> {id, status:'zaplacony', sposob, data_platnosci, kasa_operacja_id}; 400 ZLY_STATUS (koszt ogolny przez KW / juz zaplacony) */
  oznaczZaplacony: async (id, body) => {
    try {
      const response = await api.post(`external-costs/${id}/oznacz-zaplacony`, body);
      return response.data;
    } catch (error) {
      console.error('Blad oznaczania zaplaty:', error);
      throw error;
    }
  },

  // ==========================================
  // KOSZTY CYKLICZNE
  // ==========================================

  getPendingRecurring: async () => {
    try {
      const response = await api.get('external-costs/recurring/pending');
      return response.data;
    } catch (error) {
      console.error('Blad pobierania kosztow cyklicznych:', error);
      throw error;
    }
  },

  generateRecurring: async () => {
    try {
      const response = await api.post('external-costs/recurring/generate');
      return response.data;
    } catch (error) {
      console.error('Blad generowania kosztow cyklicznych:', error);
      throw error;
    }
  },

  // ==========================================
  // STATYSTYKI
  // ==========================================

  getStats: async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.location_id) params.append('location_id', filters.location_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const response = await api.get(`external-costs/stats?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Blad pobierania statystyk:', error);
      throw error;
    }
  }
};

export const profitabilityService = {
  // ==========================================
  // RENTOWNOSC
  // ==========================================

  getSummary: async (dateFrom, dateTo) => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const response = await api.get(`profitability/summary?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Blad pobierania podsumowania rentownosci:', error);
      throw error;
    }
  },

  getLocationProfitability: async (locationId, dateFrom, dateTo) => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const response = await api.get(`profitability/location/${locationId}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Blad pobierania rentownosci lokalizacji:', error);
      throw error;
    }
  },

  getReport: async (dateFrom, dateTo) => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const response = await api.get(`profitability/report?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Blad generowania raportu:', error);
      throw error;
    }
  },

  getTrend: async (locationId = null, months = 6) => {
    try {
      const params = new URLSearchParams();
      if (locationId) params.append('location_id', locationId);
      params.append('months', months);

      const response = await api.get(`profitability/trend?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Blad pobierania trendu:', error);
      throw error;
    }
  },

  getComparison: async (dateFrom, dateTo) => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const response = await api.get(`profitability/comparison?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Blad porownania:', error);
      throw error;
    }
  }
};

export default { externalCostsService, profitabilityService };
